import classNames from 'classnames';
import { type MouseEvent, memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { type Board, type PublicBoard } from '@air/api/types';
import { type JustifiedBox } from '@air/component-gallery-view';
import { Tooltip } from '@air/primitive-tooltip';
import { type CardSize } from '@air/types';

import { CustomFieldListWithOverflow } from '~/components/CustomFields/CustomFieldListWithOverflow';
import { GalleryItemType } from '~/components/Gallery/types';
import { type LibraryCustomField } from '~/components/LibraryBeta/hooks/queries/useLibraryCustomFields';
import { GalleryBoardCustomFieldAction } from '~/components/v2/components/shared/gallery/GalleryBoardCustomFieldAction';
import { GalleryBoardMenu } from '~/components/v2/components/shared/gallery/GalleryBoardMenu';
import { GalleryBoardThumbnail } from '~/components/v2/components/shared/gallery/GalleryBoardThumbnail';
import {
  GallerySelectableCard,
  type GallerySelectableCardProps,
} from '~/components/v2/components/shared/gallery/GallerySelectableCard';
import { currentCardSizeSelector, visibleCustomFieldsViewOptionsSelector } from '~/store/configViews/selectors';
import { type SelectableGalleryBoardItem } from '~/store/selectedItems/types';
import { useAirSelector } from '~/utils/ReduxUtils';

export type GalleryBoardProps<B extends Board | PublicBoard> = Pick<
  GallerySelectableCardProps<SelectableGalleryBoardItem<B>>,
  'getItemMenuOptions' | 'getSelectionMenuOptions' | 'isSelectable' | 'onClick'
> & {
  ancestors?: string;
  box: JustifiedBox;
  board: B;
  canViewCustomFields?: boolean;
  libraryCustomFields?: LibraryCustomField[];
  onCmdClickTitle?: () => void;
  size?: CardSize;
};

const _GalleryBoard = <B extends Board | PublicBoard>({
  ancestors,
  box,
  board,
  canViewCustomFields,
  getItemMenuOptions,
  getSelectionMenuOptions,
  libraryCustomFields,
  isSelectable,
  onClick,
  onCmdClickTitle,
  size = 'medium',
}: GalleryBoardProps<B>) => {
  const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);
  const configViewCardSize = useSelector(currentCardSizeSelector);
  const [hasValidThumbnail, setHasValidThumbnail] = useState(false);

  const filteredLibraryCustomFields = useMemo(() => {
    if (libraryCustomFields?.length) {
      return board?.customFields?.filter((boardField) => {
        return !!libraryCustomFields.find((customField) => customField.id === boardField.id)?.visible;
      });
    }

    return board.customFields;
  }, [board?.customFields, libraryCustomFields]);

  const filteredVisibleCustomFields = useMemo(() => {
    return filteredLibraryCustomFields?.filter((customField) =>
      visibleCustomFields.find((vcf) => vcf.customFieldId === customField.id),
    );
  }, [filteredLibraryCustomFields, visibleCustomFields]);

  const item = useMemo<SelectableGalleryBoardItem<B>>(
    () => ({
      id: board.id,
      item: board,
      type: GalleryItemType.board,
    }),
    [board],
  );

  const onTitleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if ((event.metaKey || event.ctrlKey) && !!onCmdClickTitle) {
        onCmdClickTitle();
      } else {
        onClick();
      }
    },
    [onClick, onCmdClickTitle],
  );

  const onFallbackChange = useCallback((isFallback: boolean) => {
    setHasValidThumbnail(!isFallback);
  }, []);

  return (
    <GallerySelectableCard
      data-testid="BOARD_CARD"
      className="h-full rounded-12 bg-grey-1 p-1 ring-1 ring-grey-5 ring-inset ring-offset-0 transition-colors data-[hovering=true]:bg-grey-4 data-[selected=true]:bg-grey-4 data-[highlighted=true]:ring-2 data-[highlighted=true]:ring-blue-9 data-[selected=true]:ring-2 data-[selected=true]:ring-blue-9"
      data-size={size}
      data-valid-thumbnail={hasValidThumbnail}
      getItemMenuOptions={getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      item={item}
      isSelectable={isSelectable}
      onClick={onClick}
    >
      {({ isHovering, setIsMenuOpen }) => (
        <div className="size-full">
          <GalleryBoardThumbnail board={board} box={box} sizes="100vw" onFallbackChange={onFallbackChange} />

          <div className="pointer-events-none absolute inset-x-1 top-1 flex items-center justify-between p-2">
            <div className="pointer-events-auto">
              {!!filteredLibraryCustomFields?.length && canViewCustomFields && (
                <GalleryBoardCustomFieldAction customFields={filteredLibraryCustomFields} />
              )}
            </div>

            <div className="pointer-events-auto">
              {isHovering && (
                <GalleryBoardMenu
                  board={board}
                  getItemMenuOptions={getItemMenuOptions}
                  getSelectionMenuOptions={getSelectionMenuOptions}
                  setIsMenuOpen={setIsMenuOpen}
                />
              )}
            </div>
          </div>
          <div className="pointer-events-none absolute inset-x-1 bottom-1 z-20 flex flex-col items-start gap-0.5 rounded-b-8 bg-transparent px-2 pt-6 pb-2 group-data-[valid-thumbnail=true]/gallery-selectable-card:bg-gradient-to-t group-data-[valid-thumbnail=true]/gallery-selectable-card:from-black/70 group-data-[valid-thumbnail=true]/gallery-selectable-card:to-transparent">
            <Tooltip align="start" label={board.title} side="top">
              <button
                className={classNames(
                  'pointer-events-auto max-w-full text-left font-semibold text-18 leading-[24px] hover:underline group-data-[size=small]/gallery-selectable-card:text-16 group-data-[size=small]/gallery-selectable-card:leading-[20px]',
                  hasValidThumbnail ? 'text-white' : 'text-grey-11',
                )}
                data-testid="GALLERY_BOARD_TITLE"
                onClick={onTitleClick}
                type="button"
              >
                <span
                  className={
                    configViewCardSize === 'large' || configViewCardSize === 'extra-large'
                      ? 'line-clamp-3'
                      : 'line-clamp-2'
                  }
                  style={hasValidThumbnail ? { textShadow: '0px 0px 4px rgba(0, 0, 0, 0.40)' } : undefined}
                >
                  {board.title}
                </span>
              </button>
            </Tooltip>
            {!!ancestors && (
              <span className={classNames('text-12 leading-[18px]', hasValidThumbnail ? 'text-white' : 'text-grey-11')}>
                in {ancestors}
              </span>
            )}
            {!!filteredVisibleCustomFields?.length && canViewCustomFields && (
              <CustomFieldListWithOverflow
                allowedWidth={box.width}
                customFields={filteredVisibleCustomFields}
                hasDarkBackground
                shouldShowTooltip
                size="small"
              />
            )}
          </div>
        </div>
      )}
    </GallerySelectableCard>
  );
};

export const GalleryBoard = memo(_GalleryBoard) as typeof _GalleryBoard;
